import React from 'react';
import moment from 'moment';
import { PickerColumn, readUploadedValue } from './tableColumns';

export const ACE_TABLE_KEY = 'ace-run-lines';
export const ACE_MAX_VISIBLE_COLUMNS = 12;

type Row = Record<string, any>;

export interface AceColumn extends PickerColumn<AceColumnKey> {
  header: string;
  /** Server sort key; both the run-lines and unmatched-rows endpoints accept it. */
  sortKey?: string;
  cellStyle?: React.CSSProperties;
  render: (line: Row) => React.ReactNode;
  /** Unmatched rows are the raw uploaded row, keyed by ES-003 header. */
  renderUnmatched: (raw: Row) => React.ReactNode;
}

export type AceColumnKey =
  | 'entry_date'
  | 'it_date'
  | 'broker_ref'
  | 'entry_type'
  | 'filer'
  | 'port'
  | 'mot'
  | 'liq_date'
  | 'liq_status'
  | 'psc'
  | 'importer'
  | 'importer_number'
  | 'manufacturer'
  | 'hts'
  | 'description'
  | 'coo'
  | 'coe'
  | 'spi'
  | 'quantity'
  | 'ch99_filed'
  | 'ch99_expected'
  | 'expected_base_rate'
  | 'value'
  | 'duty'
  | 'base_duty'
  | 'sec301_duty'
  | 'sec232_duty'
  | 'ieepa_duty'
  | 'sec122_duty'
  | 'adcvd_duty'
  | 'mpf'
  | 'hmf'
  | 'expected_ch99_duty';

// IT Date and Broker Reference Number are not columns BITE models — they ride
// in on the upload and survive in `source_row`, the verbatim copy of the
// customer's row that every line carries. Reading them from there means
// existing runs show them with no migration and no re-process.
const IT_DATE_HEADERS = [
  'IT Date',
  'Inbond Immediate Transportation Date',
  'Immediate Transportation Date',
];
const BROKER_REF_HEADERS = [
  'Broker Reference Number',
  'Broker Reference No',
  'Broker Reference',
  'Broker Ref Number',
  'Broker Ref No',
  'Broker Ref',
];

// Accepted spellings of an uploaded date. Nothing outside this list is
// reformatted — an unparseable value is shown exactly as uploaded rather than
// as "Invalid date". Excel serial day numbers are deliberately NOT converted:
// the processor's own serial-date branch is commented out, so converting here
// would make the table disagree with every export of the same run.
const UPLOADED_DATE_FORMATS: (string | moment.MomentBuiltinFormat)[] = [
  moment.ISO_8601,
  'YYYY-MM-DD',
  'MM/DD/YYYY',
  'M/D/YYYY',
];

const formatUploadedDate = (value: string | null): string | null => {
  if (!value) return null;
  const parsed = moment.utc(value, UPLOADED_DATE_FORMATS, true);
  return parsed.isValid() ? parsed.format('MM/DD/YYYY') : value;
};

// The server returns JSONB already parsed, but pg client config decides that —
// handle the string form too.
const parseJson = (raw: unknown): any => {
  if (typeof raw !== 'string') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const sourceRowOf = (line: Row): Row | null => {
  const row = parseJson(line?.source_row);
  return row && typeof row === 'object' && !Array.isArray(row) ? row : null;
};

const jsonArray = (raw: unknown): any[] => {
  const parsed = parseJson(raw);
  return Array.isArray(parsed) ? parsed : [];
};

/** The uploaded IT Date for this line as MM/DD/YYYY, or null when the upload had none. */
export const extractItDate = (line: Row): string | null =>
  formatUploadedDate(readUploadedValue(sourceRowOf(line), IT_DATE_HEADERS));

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const toNumber = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const money = (v: unknown): string => {
  const n = toNumber(v);
  return n === null ? '--' : usd.format(n);
};

/** Sum of the amounts, or '--' when none is present. */
const moneySum = (values: unknown[]): string => {
  const nums = values.map(toNumber).filter((n): n is number => n !== null);
  return nums.length ? usd.format(nums.reduce((a, b) => a + b, 0)) : '--';
};

// The ACE export's dates are calendar dates, not moments in time. Format in UTC
// so a viewer west of UTC doesn't see them shift back a day.
const date = (v: unknown): string =>
  v ? moment.utc(v as string).format('MM/DD/YYYY') : '--';

const text = (v: unknown): string =>
  v === null || v === undefined || String(v).trim() === '' ? '--' : String(v);

const quantity = (qty: unknown, uom: unknown): string => {
  const n = toNumber(qty);
  if (n === null) return '--';
  const amount = n.toLocaleString('en-US', { maximumFractionDigits: 5 });
  return uom ? `${amount} ${uom}` : amount;
};

// Per-program Ch.99 duty. The scalar `<prefix>_duty` columns hold only the
// first code filed for a program; tariff_hits carries every one.
const programDuty = (line: Row, matches: (prefix: string) => boolean): string =>
  moneySum(
    jsonArray(line.tariff_hits)
      .filter((h) => matches(String(h?.prefix || '')))
      .map((h) => h?.duty),
  );

const codeList = (codes: string[]): React.ReactNode =>
  codes.length ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      {codes.map((c) => (
        <span key={c} style={{ whiteSpace: 'nowrap' }}>
          {c}
        </span>
      ))}
    </div>
  ) : (
    '--'
  );

const uniqueCodes = (rows: any[]): string[] =>
  Array.from(new Set(rows.map((r) => String(r?.hts || '')).filter(Boolean)));

const NOT_ANALYZED = (): string => '--';

const nowrap: React.CSSProperties = { whiteSpace: 'nowrap' };

export const ACE_COLUMNS: readonly AceColumn[] = [
  {
    key: 'entry_date',
    label: 'Entry Date',
    group: 'Entry',
    defaultVisible: true,
    header: 'ENTRY DATE',
    sortKey: 'entry_date',
    render: (l) => date(l.entry_date),
    renderUnmatched: (r) => date(r['Entry Date']),
  },
  {
    key: 'it_date',
    label: 'IT Date',
    group: 'Entry',
    header: 'IT DATE',
    cellStyle: nowrap,
    render: (l) => extractItDate(l) || '--',
    renderUnmatched: (r) =>
      formatUploadedDate(readUploadedValue(r, IT_DATE_HEADERS)) || '--',
  },
  {
    key: 'broker_ref',
    label: 'Broker Reference #',
    group: 'Entry',
    header: 'BROKER REF #',
    cellStyle: nowrap,
    render: (l) =>
      readUploadedValue(sourceRowOf(l), BROKER_REF_HEADERS) || '--',
    renderUnmatched: (r) => readUploadedValue(r, BROKER_REF_HEADERS) || '--',
  },
  {
    key: 'entry_type',
    label: 'Entry Type',
    group: 'Entry',
    header: 'ENTRY TYPE',
    sortKey: 'entry_type_code',
    render: (l) => (
      <span title={l.entry_type_short_description || undefined}>
        {text(l.entry_type_code)}
      </span>
    ),
    renderUnmatched: (r) => text(r['Entry Type Code']),
  },
  {
    key: 'filer',
    label: 'Filer',
    group: 'Entry',
    header: 'FILER',
    sortKey: 'filer_code',
    render: (l) => (
      <span title={l.filer_name || undefined}>{text(l.filer_code)}</span>
    ),
    renderUnmatched: (r) => text(r['Filer Code']),
  },
  {
    key: 'port',
    label: 'Port of Entry',
    group: 'Entry',
    header: 'PORT',
    sortKey: 'port_of_entry_code',
    render: (l) => (
      <span title={l.actual_port_of_entry_name || undefined}>
        {text(l.port_of_entry_code)}
      </span>
    ),
    renderUnmatched: (r) => text(r['Port of Entry Code']),
  },
  {
    key: 'mot',
    label: 'Mode of Transport',
    group: 'Entry',
    header: 'MOT',
    sortKey: 'mode_of_transportation_code',
    render: (l) => text(l.mode_of_transportation_code),
    renderUnmatched: (r) => text(r['Mode of Transportation Code']),
  },
  {
    key: 'liq_date',
    label: 'Liquidation Date',
    group: 'Entry',
    header: 'LIQ. DATE',
    sortKey: 'liquidation_date',
    render: (l) => date(l.liquidation_date),
    renderUnmatched: (r) => date(r['Liquidation Date']),
  },
  {
    key: 'liq_status',
    label: 'Liquidation Status',
    group: 'Entry',
    header: 'LIQ. STATUS',
    sortKey: 'liquidation_status',
    render: (l) => text(l.liquidation_status),
    renderUnmatched: (r) => text(r['Liquidation Status']),
  },
  {
    key: 'psc',
    label: 'PSC Filed',
    group: 'Entry',
    header: 'PSC',
    sortKey: 'psc_indicator',
    render: (l) => text(l.psc_indicator),
    renderUnmatched: (r) => text(r['PSC Indicator']),
  },
  {
    key: 'importer',
    label: 'Importer',
    group: 'Parties',
    defaultVisible: true,
    header: 'IMPORTER',
    sortKey: 'importer_name',
    render: (l) => l.importer_name || '--',
    renderUnmatched: (r) => r['Importer Name'] || '--',
  },
  {
    key: 'importer_number',
    label: 'Importer #',
    group: 'Parties',
    header: 'IMPORTER #',
    sortKey: 'importer_number',
    cellStyle: nowrap,
    render: (l) => text(l.importer_number),
    renderUnmatched: (r) => text(r['Importer Number']),
  },
  {
    key: 'manufacturer',
    label: 'Manufacturer (MID)',
    group: 'Parties',
    header: 'MID',
    sortKey: 'manufacturer_id',
    cellStyle: nowrap,
    render: (l) => (
      <span title={l.manufacturer_name || undefined}>
        {text(l.manufacturer_id)}
      </span>
    ),
    renderUnmatched: (r) => text(r['Manufacturer ID']),
  },
  {
    key: 'hts',
    label: 'HTS',
    group: 'Classification',
    defaultVisible: true,
    header: 'HTS',
    sortKey: 'hts_number_full',
    render: (l) => l.hts_number_full || l.ch98_hts2 || '--',
    renderUnmatched: (r) => r['HTS Number - Full'] || r['HTS Number'] || '--',
  },
  {
    key: 'description',
    label: 'Description',
    group: 'Classification',
    defaultVisible: true,
    header: 'DESCRIPTION',
    cellStyle: {
      maxWidth: 200,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    render: (l) =>
      (l.hts_short_description || l.ch98_hts2_desc)?.toLowerCase() || '--',
    renderUnmatched: (r) => r['HTS Short Description']?.toLowerCase() || '--',
  },
  {
    key: 'coo',
    label: 'Country of Origin',
    group: 'Classification',
    defaultVisible: true,
    header: 'COO',
    sortKey: 'country_of_origin_code',
    render: (l) => l.country_of_origin_code || '--',
    renderUnmatched: (r) => r['Country of Origin Code'] || '--',
  },
  {
    key: 'coe',
    label: 'Country of Export',
    group: 'Classification',
    header: 'COE',
    sortKey: 'country_of_export_code',
    render: (l) => text(l.country_of_export_code),
    renderUnmatched: (r) => text(r['Country of Export Code']),
  },
  {
    key: 'spi',
    label: 'SPI',
    group: 'Classification',
    defaultVisible: true,
    header: 'SPI',
    sortKey: 'line_spi_code',
    cellStyle: { fontFamily: 'Inter', whiteSpace: 'nowrap' },
    render: (l) => l.line_spi_code || '--',
    renderUnmatched: (r) => r['Line SPI Code'] || '--',
  },
  {
    key: 'quantity',
    label: 'Quantity',
    group: 'Classification',
    header: 'QTY',
    sortKey: 'line_tariff_quantity_1',
    cellStyle: nowrap,
    render: (l) => quantity(l.line_tariff_quantity_1, l.line_tariff_uom_1_code),
    renderUnmatched: (r) =>
      quantity(r['Line Tariff Quantity (1)'], r['Line Tariff UOM (1) Code']),
  },
  {
    key: 'ch99_filed',
    label: 'Ch. 99 Filed',
    group: 'Classification',
    header: 'CH. 99 FILED',
    render: (l) =>
      codeList(
        uniqueCodes(
          [...jsonArray(l.tariff_hits)].sort(
            (a, b) => (Number(a?.ordinal) || 0) - (Number(b?.ordinal) || 0),
          ),
        ),
      ),
    renderUnmatched: NOT_ANALYZED,
  },
  {
    key: 'ch99_expected',
    label: 'BITE Expected Ch. 99',
    group: 'Classification',
    header: 'BITE CH. 99',
    render: (l) =>
      codeList(
        uniqueCodes(
          [...jsonArray(l.expected_tariffs)].sort(
            (a, b) =>
              (Number(a?.expectedOrdinal) || 0) -
                (Number(b?.expectedOrdinal) || 0) ||
              String(a?.hts || '').localeCompare(String(b?.hts || '')),
          ),
        ),
      ),
    renderUnmatched: NOT_ANALYZED,
  },
  {
    key: 'expected_base_rate',
    label: 'Expected Base Rate',
    group: 'Classification',
    header: 'EXP. BASE RATE',
    cellStyle: nowrap,
    render: (l) => text(l.expected_base_rate),
    renderUnmatched: NOT_ANALYZED,
  },
  {
    key: 'value',
    label: 'Entered Value',
    group: 'Values & Duties',
    defaultVisible: true,
    header: 'VALUE',
    sortKey: 'line_tariff_goods_value_amount',
    render: (l) =>
      l.line_tariff_goods_value_amount || l.ch98_hts2_value
        ? usd.format(l.line_tariff_goods_value_amount || l.ch98_hts2_value)
        : '--',
    renderUnmatched: (r) =>
      r['Line Tariff Goods Value Amount']
        ? usd.format(r['Line Tariff Goods Value Amount'])
        : '--',
  },
  {
    key: 'duty',
    label: 'Total Duty',
    group: 'Values & Duties',
    defaultVisible: true,
    header: 'DUTY',
    sortKey: 'total_duty',
    render: (l) =>
      l.total_duty !== null && l.total_duty !== undefined
        ? usd.format(l.total_duty)
        : '--',
    renderUnmatched: (r) =>
      r['Line Tariff Duty Amount']
        ? usd.format(r['Line Tariff Duty Amount'])
        : '--',
  },
  {
    key: 'base_duty',
    label: 'Base Duty',
    group: 'Values & Duties',
    header: 'BASE DUTY',
    sortKey: 'line_tariff_duty_amount',
    render: (l) => money(l.line_tariff_duty_amount),
    renderUnmatched: (r) => money(r['Line Tariff Duty Amount']),
  },
  {
    key: 'sec301_duty',
    label: 'Section 301 Duty',
    group: 'Values & Duties',
    header: '301 DUTY',
    render: (l) =>
      programDuty(l, (p) => p === 'sec_301' || p === 'sec_301_flip'),
    renderUnmatched: NOT_ANALYZED,
  },
  {
    key: 'sec232_duty',
    label: 'Section 232 Duty',
    group: 'Values & Duties',
    header: '232 DUTY',
    render: (l) => programDuty(l, (p) => p.startsWith('sec_232_')),
    renderUnmatched: NOT_ANALYZED,
  },
  {
    key: 'ieepa_duty',
    label: 'IEEPA Duty',
    group: 'Values & Duties',
    header: 'IEEPA DUTY',
    render: (l) => programDuty(l, (p) => p.startsWith('ieepa_')),
    renderUnmatched: NOT_ANALYZED,
  },
  {
    key: 'sec122_duty',
    label: 'Section 122 Duty',
    group: 'Values & Duties',
    header: '122 DUTY',
    render: (l) => programDuty(l, (p) => p === 'sec_122'),
    renderUnmatched: NOT_ANALYZED,
  },
  {
    key: 'adcvd_duty',
    label: 'AD/CVD Duty',
    group: 'Values & Duties',
    header: 'AD/CVD',
    sortKey: 'adcvd_duty',
    render: (l) =>
      moneySum([l.antidumping_duty_amount, l.countervailing_duty_amount]),
    renderUnmatched: (r) =>
      moneySum([r['Antidumping Duty Amount'], r['Countervailing Duty Amount']]),
  },
  {
    key: 'mpf',
    label: 'MPF',
    group: 'Values & Duties',
    header: 'MPF',
    sortKey: 'line_mpf_amount',
    render: (l) => money(l.line_mpf_amount),
    renderUnmatched: (r) => money(r['Line MPF Amount']),
  },
  {
    key: 'hmf',
    label: 'HMF',
    group: 'Values & Duties',
    header: 'HMF',
    sortKey: 'line_hmf_amount',
    render: (l) => money(l.line_hmf_amount),
    renderUnmatched: (r) => money(r['Line HMF Amount']),
  },
  {
    key: 'expected_ch99_duty',
    label: 'BITE Expected Ch. 99 Duty',
    group: 'Values & Duties',
    header: 'BITE CH. 99 DUTY',
    render: (l) => moneySum(jsonArray(l.expected_tariffs).map((t) => t?.duty)),
    renderUnmatched: NOT_ANALYZED,
  },
];
